import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { NotificationEmailPreferences, NotificationPreference } from '../models/notification-preference.model';
import { NotificationType } from '../models/notification.model';

/**
 * The current user's notification delivery preferences.
 *
 * Cached in a BehaviorSubject because the delivery path consults them on every incoming
 * notification — going to the network there would put an HTTP round trip in front of showing a
 * toast, and would hammer the API during a burst.
 */
@Injectable({ providedIn: 'root' })
export class NotificationPreferencesService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiConfig.corporateAPI.endpoint}/notifications/preferences`;

  private readonly preferencesSubject = new BehaviorSubject<NotificationPreference[]>([]);
  readonly preferences$ = this.preferencesSubject.asObservable();

  private loaded = false;

  /** Loads once per session unless `force` is set. Safe to call from several places. */
  load(force = false): Observable<NotificationPreference[]> {
    if (this.loaded && !force) return of(this.preferencesSubject.value);

    return this.http.get<NotificationPreference[]>(this.baseUrl).pipe(
      catchError(() => of([] as NotificationPreference[])),
      tap(preferences => {
        this.loaded = true;
        this.preferencesSubject.next(preferences);
      }),
    );
  }

  save(preferences: NotificationPreference[]): Observable<NotificationPreference[]> {
    return this.http.put<NotificationPreference[]>(this.baseUrl, preferences).pipe(
      tap(saved => this.preferencesSubject.next(saved)),
    );
  }

  /**
   * The user's email switch per organisation (GET notifications/preferences/email). Null on a
   * failure or an older backend (404), so the Settings page hides the switch instead of faking it.
   */
  getEmailPreferences(): Observable<NotificationEmailPreferences | null> {
    return this.http.get<NotificationEmailPreferences>(`${this.baseUrl}/email`).pipe(
      catchError(() => of(null)),
    );
  }

  /**
   * Sets one organisation's email switch (companyId), or the global default (companyId null).
   * email = null clears the choice back to the default. Returns the full updated settings.
   */
  setEmailPreference(companyId: number | null, email: boolean | null): Observable<NotificationEmailPreferences> {
    return this.http.put<NotificationEmailPreferences>(`${this.baseUrl}/email`, { companyId, email });
  }

  /**
   * Whether this notification kind should be shown on screen.
   *
   * Defaults to true for an unknown or not-yet-loaded type. The server sends the full list with
   * defaults filled in, so the only way to reach here empty is a failed load — and silently
   * swallowing notifications because a settings request failed would be the worse outcome.
   */
  showsOnScreen(type: NotificationType | undefined): boolean {
    if (!type) return true;

    const preferences = this.preferencesSubject.value;
    if (preferences.length === 0) return true;

    return preferences.find(p => p.key === type)?.onScreen ?? true;
  }
}
